import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, orderBy, limit, startAfter, getDocs, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsEurope } from '../../firebase';
import { notifySuccess, notifyError } from '../../utils/notify';
import { ArrowTopRightOnSquareIcon, MagnifyingGlassIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;
const STATUS_FILTERS = ['all', 'published', 'draft', 'paused', 'completed'];
const SORT_OPTIONS = [
  { value: 'created', label: 'Newest' },
  { value: 'plays', label: 'Most plays' },
  { value: 'name', label: 'Name A\u2013Z' },
];

const ACTION_LABELS = {
  unpublish: 'Unpublish',
  complete: 'Complete',
  refundCredit: 'Refund Credit',
};

function parseDate(createdAt) {
  if (!createdAt) return null;
  if (typeof createdAt?.toDate === 'function') return createdAt.toDate();
  if (typeof createdAt?.seconds === 'number') return new Date(createdAt.seconds * 1000);
  const date = new Date(createdAt);
  return isNaN(date.getTime()) ? null : date;
}

function formatDate(d) {
  if (!d) return '\u2014';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const statusColor = (status) => {
  switch (status) {
    case 'published': return 'bg-green-500/20 text-green-300';
    case 'draft': return 'bg-white/10 gch-text-muted';
    case 'paused': return 'bg-yellow-500/20 text-yellow-300';
    case 'completed': return 'bg-blue-500/20 text-blue-300';
    default: return 'bg-white/10 gch-text-muted';
  }
};

function ActionMenu({ campaign, onAction, openMenuId, setOpenMenuId }) {
  const menuRef = useRef(null);
  const isOpen = openMenuId === campaign.id;
  const status = campaign.status || 'draft';

  // Build available actions based on status
  const actions = [];
  if (status === 'published' || status === 'paused') {
    actions.push('unpublish', 'complete');
  }
  actions.push('refundCredit');

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, setOpenMenuId]);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : campaign.id); }}
        className="p-1 rounded-[var(--admin-card-radius)] hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
        title="Actions"
      >
        <EllipsisVerticalIcon className="w-4 h-4 gch-text-secondary" />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[140px] py-1 rounded-lg bg-[var(--admin-bg-surface)] border border-[var(--admin-border)] shadow-lg">
          {actions.map(action => (
            <button
              key={action}
              onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onAction(campaign.id, action); }}
              className="w-full text-left px-3 py-2 text-xs gch-text-secondary hover:text-white hover:bg-white/5 transition-colors"
            >
              {ACTION_LABELS[action]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created');
  const [statsMap, setStatsMap] = useState({});
  const [workspaceNames, setWorkspaceNames] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const loadCampaigns = useCallback(async (afterDoc = null) => {
    try {
      const constraints = [orderBy('createdAt', 'desc'), limit(PAGE_SIZE)];
      if (afterDoc) {
        constraints.push(startAfter(afterDoc));
      }
      const q = query(collection(db, 'campaign'), ...constraints);
      const snap = await getDocs(q);
      const newCampaigns = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      setHasMore(snap.docs.length === PAGE_SIZE);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      if (afterDoc) {
        setCampaigns(prev => [...prev, ...newCampaigns]);
      } else {
        setCampaigns(newCampaigns);
      }

      // Extract play counts from campaign docs (stats are on the campaign collection)
      const newStats = {};
      for (const c of newCampaigns) {
        newStats[c.id] = c.stats?.totalPlays || 0;
      }
      setStatsMap(prev => ({ ...prev, ...newStats }));

      // Fetch workspace names
      const companyIds = [...new Set(newCampaigns.map(c => c.companyId).filter(Boolean))];
      fetchWorkspaceNames(companyIds);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkspaceNames = async (companyIds) => {
    const results = {};
    for (let i = 0; i < companyIds.length; i += 10) {
      const batch = companyIds.slice(i, i + 10);
      await Promise.all(batch.map(async (id) => {
        try {
          const wsDoc = await getDoc(doc(db, 'workspace', id));
          results[id] = wsDoc.exists() ? (wsDoc.data().companyName || wsDoc.data().name || id) : id;
        } catch {
          results[id] = id;
        }
      }));
    }
    setWorkspaceNames(prev => ({ ...prev, ...results }));
  };

  useEffect(() => {
    loadCampaigns(null);
  }, [loadCampaigns]);

  const handleAction = async (campaignId, action) => {
    if (actionLoading) return;
    if (!window.confirm(`${ACTION_LABELS[action]} this campaign? This cannot be undone.`)) return;
    setActionLoading(`${campaignId}_${action}`);
    try {
      await httpsCallable(functionsEurope, 'adminCampaignAction')({ campaignId, action });
      notifySuccess(`${ACTION_LABELS[action]} successful`);
      // Update local state
      setCampaigns(prev => prev.map(c => {
        if (c.id !== campaignId) return c;
        if (action === 'unpublish') return { ...c, status: 'draft' };
        if (action === 'complete') return { ...c, status: 'completed' };
        return c; // refundCredit doesn't change status
      }));
    } catch (error) {
      notifyError(error.message || `Failed: ${ACTION_LABELS[action]}`);
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => {
    let result = campaigns;

    if (statusFilter !== 'all') {
      result = result.filter(c => (c.status || 'draft') === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c => {
        const title = (c.internalName || c.options?.title || c.name || '').toLowerCase();
        return title.includes(q) || c.id.toLowerCase().includes(q);
      });
    }

    if (sortBy === 'created') {
      result = [...result].sort((a, b) => {
        const da = parseDate(a.createdAt);
        const db = parseDate(b.createdAt);
        return (db?.getTime() || 0) - (da?.getTime() || 0);
      });
    } else if (sortBy === 'plays') {
      result = [...result].sort((a, b) => (statsMap[b.id] || 0) - (statsMap[a.id] || 0));
    } else if (sortBy === 'name') {
      result = [...result].sort((a, b) => {
        const na = (a.internalName || a.options?.title || a.name || '').toLowerCase();
        const nb = (b.internalName || b.options?.title || b.name || '').toLowerCase();
        return na.localeCompare(nb);
      });
    }

    return result;
  }, [campaigns, statusFilter, search, sortBy, statsMap]);

  if (loading && campaigns.length === 0) {
    return <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-10 animate-pulse bg-white/5 rounded-[var(--admin-card-radius)]" />)}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 gch-text-muted pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none"
          />
        </div>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none cursor-pointer"
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
        {STATUS_FILTERS.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-[var(--admin-card-radius)] text-xs font-medium capitalize whitespace-nowrap transition-colors border ${
              statusFilter === s
                ? 'bg-white/10 text-white border-white/20'
                : 'gch-text-muted border-transparent hover:text-white hover:bg-white/5'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <p className="text-xs gch-text-muted">{filtered.length} campaign{filtered.length !== 1 ? 's' : ''}{search ? ' matching' : ''}</p>

      {/* Table header — desktop only */}
      <div className="hidden lg:grid grid-cols-[2fr_120px_72px_64px_56px_64px_56px] gap-3 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider gch-text-muted select-none">
        <span>Name</span>
        <span>Customer</span>
        <span>Status</span>
        <span className="text-right">Plays</span>
        <span>Type</span>
        <span>Created</span>
        <span />
      </div>

      {/* Rows */}
      <div className="divide-y divide-[var(--admin-border)]">
        {filtered.map(c => {
          const title = c.internalName || c.options?.title || c.name || 'Untitled';
          const created = parseDate(c.createdAt);
          const plays = statsMap[c.id];
          const gameType = c.gameType || c.options?.gameType || '';
          const customerName = workspaceNames[c.companyId] || c.companyId || '\u2014';
          return (
            <div key={c.id} className="group grid grid-cols-[1fr_auto_auto] lg:grid-cols-[2fr_120px_72px_64px_56px_64px_56px] gap-3 items-center px-4 py-2.5 hover:bg-white/[0.03] transition-colors rounded-[var(--admin-card-radius)]">
              {/* Name + ID */}
              <div className="min-w-0">
                <p className="text-sm font-medium gch-text-primary truncate">{title}</p>
                <p className="text-[10px] gch-text-muted font-mono truncate mt-0.5">{c.id}</p>
              </div>
              {/* Customer — desktop */}
              <p className="text-xs gch-text-secondary truncate hidden lg:block" title={customerName}>{customerName}</p>
              {/* Status */}
              <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full text-center leading-tight ${statusColor(c.status)}`}>
                {c.status || 'draft'}
              </span>
              {/* Plays — desktop */}
              <p className="text-xs gch-text-secondary text-right tabular-nums hidden lg:block">
                {plays != null ? plays.toLocaleString() : '\u2014'}
              </p>
              {/* Type — desktop */}
              <p className="text-[11px] gch-text-muted truncate capitalize hidden lg:block">{gameType || '\u2014'}</p>
              {/* Created — desktop */}
              <p className="text-[11px] gch-text-muted hidden lg:block">{formatDate(created)}</p>
              {/* Actions */}
              <div className="flex items-center justify-end gap-1">
                {(c.status === 'published' || c.status === 'paused') && (
                  <a
                    href={`/p/${c.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 rounded-[var(--admin-card-radius)] hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
                    title="Open game"
                  >
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 gch-text-secondary" />
                  </a>
                )}
                <ActionMenu
                  campaign={c}
                  onAction={handleAction}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                />
              </div>
            </div>
          );
        })}
      </div>

      {hasMore && !search && (
        <button
          onClick={() => loadCampaigns(lastDoc)}
          className="w-full btn-admin-outline text-sm py-2.5 rounded-[var(--admin-card-radius)]"
        >
          Load more
        </button>
      )}

      {filtered.length === 0 && (
        <p className="text-sm gch-text-muted text-center py-8">
          {search ? 'No campaigns match your search.' : 'No campaigns found.'}
        </p>
      )}
    </div>
  );
}
